import { useEffect, useState } from 'react';
import { isTauri, initDatabase, getLicenseInfo, getDeviceFingerprint } from '@/lib/tauri';
import { useLicenseStore } from '@/stores/licenseStore';
import { useSyncStore } from '@/stores/syncStore';

export interface DesktopInitStatus {
  isDesktop: boolean;
  isInitialized: boolean;
  needsOnboarding: boolean;
  error?: string;
}

/**
 * Initialize desktop app on startup
 * - Create local database
 * - Check license status
 * - Determine if onboarding is needed
 */
export function useDesktopInit() {
  const [status, setStatus] = useState<DesktopInitStatus>({
    isDesktop: false,
    isInitialized: false,
    needsOnboarding: false,
  });

  const { setLicense, setLicenseState } = useLicenseStore();

  useEffect(() => {
    async function initialize() {
      // Check if running in Tauri
      const isDesktop = isTauri();

      if (!isDesktop) {
        setStatus({ isDesktop: false, isInitialized: true, needsOnboarding: false });
        return;
      }

      try {
        console.log('🖥️ Initializing desktop app...');

        // Step 1: Initialize local database
        console.log('📦 Creating local database...');
        await initDatabase();
        console.log('✅ Local database created');

        // Step 2: Get device fingerprint
        const fingerprint = await getDeviceFingerprint();
        console.log('🔑 Device fingerprint:', fingerprint);

        // Step 3: Check for existing license
        console.log('🎫 Checking for license...');
        const licenseInfo = await getLicenseInfo();

        if (!licenseInfo) {
          // No license found - needs onboarding
          console.log('⚠️ No license found - onboarding required');
          setStatus({
            isDesktop: true,
            isInitialized: true,
            needsOnboarding: true,
          });
          setLicenseState('verification_needed');
        } else {
          // License found - set it in store (Tauri returns nested structure)
          const licenseKey = licenseInfo.license?.license_key || licenseInfo.license_key;
          console.log('✅ License found:', licenseKey);
          setLicense(licenseInfo);
          setStatus({
            isDesktop: true,
            isInitialized: true,
            needsOnboarding: false,
          });
        }
      } catch (error: any) {
        console.error('❌ Desktop initialization failed:', error);
        setStatus({
          isDesktop: true,
          isInitialized: false,
          needsOnboarding: false,
          error: error.message,
        });
      }
    }

    initialize();
  }, [setLicense, setLicenseState]);

  return status;
}
